import { FocusEvent, useEffect, useState } from 'react';
import { FormikErrors } from 'formik';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { Logger } from '@utils/logger';
import { getIDPropertyType } from '@utils/id-options-util';
import { SelectOption } from '@iddataweb/idw-common-ui/types';
import { AXNUISelectInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';
import { useSelector } from 'react-redux';
import { getLanguageContent } from '@redux/selectors/language-selectors';

type IDSelectInputProps = {
    title: string;
    attribute: AttributeFormState;
    fieldName: string;
    fieldTouched: boolean;
    fieldError: string;
    fieldIsInvalid: boolean;
    fieldShouldBeHighlighted: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
    idOptions: Array<SelectOption>;
};

export const IDSelectInput = (props: IDSelectInputProps) => {
    const {
        title,
        attribute,
        fieldName,
        fieldTouched,
        fieldError,
        fieldIsInvalid,
        fieldShouldBeHighlighted,
        setFieldValue,
        setFieldTouched,
        onBlur,
        onSetSubmitValue,
        isSubmitting,
        idOptions
    } = props;

    const idPropertyType = getIDPropertyType(attribute.type);
    const content = useSelector(getLanguageContent);
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        if (idOptions?.length > 0) {
            handleChange(idOptions[0]);
            Logger.info(`[IDSelectInput] Set default value ${idOptions[0].value}`);
        }
    }, [attribute.type, idOptions]);

    useEffect(() => {
        if (attribute.inputData[idPropertyType]) {
            const option = idOptions.find(option => option.value === attribute.inputData[idPropertyType]);
            if (option) {
                setSelectedOption(option);
            }
        }
    }, [attribute.inputData[idPropertyType], content]);

    const handleChange = async (newValue: SelectOption) => {
        Logger.info(`[${idPropertyType}Attribute] Selected ID Type: `, newValue);

        if (newValue) {
            const { value } = newValue;
            onSetSubmitValue({ inputData: { [idPropertyType]: value } });
            await setFieldValue(fieldName, value);
            setFieldTouched(fieldName, true, true);
        }
    };

    return (
        <AXNUISelectInputWithErrorMessage
            controlId={getIDPropertyType(attribute.type)}
            automationId={getIDPropertyType(attribute.type)}
            ariaLabel={content.validation.idSelection.required}
            isClearable={false}
            isSearchable={false}
            title={title}
            placeholder={attribute.placeholder
                ? attribute.placeholder
                : content.validation.idSelection.required}
            selectedOption={selectedOption}
            options={idOptions}
            fieldName={fieldName}
            fieldTouched={fieldTouched}
            fieldError={fieldError}
            fieldIsInvalid={fieldIsInvalid}
            fieldShouldBeHighlighted={fieldShouldBeHighlighted}
            onBlur={onBlur}
            setFieldTouched={setFieldTouched}
            onChange={handleChange}
            isDisabled={attribute.isDisabled}
			isPlaceholder={attribute.isPlaceholder}
			isOptional={attribute.optional}
            isSubmitting={isSubmitting}
        />
    );
};